import React, { useCallback, useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { capitalCase } from 'change-case';
import { Helmet } from 'react-helmet';
import { AlineInput } from 'src/AlineInput';
import { AlineButton, AlineDialog } from 'src/components';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDispatchNotification } from 'src/helpers';
import { createDataGridPremiumFooterlessPaperStyles } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { UpgradeRequestActionsCell } from './upgrade-request-actions-cell';

const useStyles = makeStyles()(theme => ({
  denyActions: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  denyPaper: {
    width: 480,
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  reasonLabel: {
    fontSize: 13,
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(1),
  },
  requesterCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
  },
  requesterEmail: {
    color: theme.palette.text.secondary,
  },
  xGrid: {
    flexGrow: 1,
    minHeight: 0,
  },
}));

type UpgradeRequestRow = Apollo.UpgradeRequestBaseFragment;

interface UpgradeRequestDenyDialogProps {
  onClose: () => void;
  onRequestUpdated: () => void;
  requestId: string | null;
}

const UpgradeRequestDenyDialog = React.memo(function UpgradeRequestDenyDialog({
  onClose,
  onRequestUpdated,
  requestId,
}: UpgradeRequestDenyDialogProps): JSX.Element {
  const { classes } = useStyles();
  const [denyReason, setDenyReason] = useState('');
  const [respondToUpgradeRequest, { loading: responding }] =
    Apollo.useRespondToUpgradeRequestMutation();

  const handleClose = useCallback(() => {
    setDenyReason('');
    onClose();
  }, [onClose]);

  const handleConfirmDeny = useCallback(async () => {
    if (!requestId) return;
    const { data } = await respondToUpgradeRequest({
      variables: {
        input: {
          approve: false,
          id: requestId,
          responseMessage: denyReason || undefined,
        },
      },
    });
    if (!data?.respondToUpgradeRequest) return;
    onRequestUpdated();
    handleClose();
  }, [denyReason, handleClose, onRequestUpdated, requestId, respondToUpgradeRequest]);

  return (
    <AlineDialog
      classes={{ paper: classes.denyPaper }}
      open={Boolean(requestId)}
      title="Deny upgrade request"
      onClose={handleClose}
    >
      <AlineInput
        fullWidth
        multiline
        minRows={3}
        placeholder="Type a message to send to the requester..."
        value={denyReason}
        onChange={event => setDenyReason(event.target.value)}
      />
      <div className={classes.denyActions}>
        <AlineButton color="primary" variant="outlined" onClick={handleClose}>
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          loading={responding}
          variant="contained"
          onClick={handleConfirmDeny}
        >
          Deny
        </AlineButton>
      </div>
    </AlineDialog>
  );
});

export function UpgradeRequestsPage(): JSX.Element {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const isAdmin = currentUser?.role === Apollo.OrgRole.Admin;
  const [denyRequestId, setDenyRequestId] = useState<string | null>(null);
  const { data, loading, refetch } = Apollo.useGetUpgradeRequestsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const dispatchUpdatedNotification = useDispatchNotification({
    message: 'Upgrade request updated.',
    variant: 'success',
  });

  const closeDenyDialog = useCallback(() => {
    setDenyRequestId(null);
  }, []);

  const handleReject = useCallback((requestId: string) => {
    setDenyRequestId(requestId);
  }, []);

  const handleRequestUpdated = useCallback(() => {
    dispatchUpdatedNotification();
    refetch();
  }, [dispatchUpdatedNotification, refetch]);

  const columns = useMemo<GridColDef<UpgradeRequestRow>[]>(
    () => [
      {
        field: 'requester',
        flex: 1.2,
        headerName: 'Requester',
        minWidth: 180,
        renderCell: ({ row }: GridRenderCellParams<UpgradeRequestRow>) => {
          const { primary, secondary } = getRequesterDisplayDetails(row.requester);
          return (
            <div className={classes.requesterCell}>
              <Typography noWrap variant="body2">
                {primary}
              </Typography>
              {secondary && (
                <Typography noWrap className={classes.requesterEmail} variant="caption">
                  {secondary}
                </Typography>
              )}
            </div>
          );
        },
      },
      {
        field: 'requestedRole',
        flex: 0.7,
        headerName: 'Requested Role',
        minWidth: 140,
        renderCell: ({ row }: GridRenderCellParams<UpgradeRequestRow>) =>
          capitalCase(row.requestedRole),
      },
      {
        field: 'message',
        flex: 1.5,
        headerName: 'Message',
        minWidth: 180,
        renderCell: ({ row }: GridRenderCellParams<UpgradeRequestRow>) => row.message || '-',
      },
      {
        field: 'status',
        flex: 0.7,
        headerName: 'Status',
        minWidth: 110,
        renderCell: ({ row }: GridRenderCellParams<UpgradeRequestRow>) => {
          switch (row.status) {
            case Apollo.UpgradeRequestStatus.Pending:
              return 'Pending';
            case Apollo.UpgradeRequestStatus.Approved:
              return 'Approved';
            case Apollo.UpgradeRequestStatus.Denied:
              return 'Denied';
            case Apollo.UpgradeRequestStatus.Cancelled:
              return 'Cancelled';
          }
        },
      },
      {
        field: 'createdAt',
        flex: 0.8,
        headerName: 'Created',
        minWidth: 120,
        renderCell: ({ row }: GridRenderCellParams<UpgradeRequestRow>) =>
          new Date(row.createdAt).toLocaleDateString(),
      },
      {
        field: 'actions',
        flex: 0.5,
        headerName: 'Actions',
        minWidth: 90,
        renderCell: ({ row }: GridRenderCellParams<UpgradeRequestRow>) => (
          <UpgradeRequestActionsCell
            isAdmin={isAdmin}
            requestId={row.id}
            status={row.status}
            onReject={handleReject}
            onRequestUpdated={handleRequestUpdated}
          />
        ),
        sortable: false,
      },
    ],
    [classes.requesterCell, classes.requesterEmail, handleReject, handleRequestUpdated, isAdmin]
  );

  return (
    <div className={classes.listContainer}>
      <Helmet>
        <title>Upgrade Requests</title>
      </Helmet>
      <DataGridPremium
        disableColumnFilter
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        disableRowSelectionOnClick
        hideFooter
        className={classes.xGrid}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: 'createdAt', sort: 'desc' }],
          },
        }}
        loading={loading}
        rows={data?.upgradeRequests ?? []}
        slotProps={{
          loadingOverlay: {
            noRowsVariant: 'linear-progress',
            variant: 'linear-progress',
          },
        }}
        sx={createDataGridPremiumFooterlessPaperStyles}
      />
      <UpgradeRequestDenyDialog
        requestId={denyRequestId}
        onClose={closeDenyDialog}
        onRequestUpdated={handleRequestUpdated}
      />
    </div>
  );
}

function getRequesterDisplayDetails(requester: UpgradeRequestRow['requester']) {
  const name = [requester.firstName, requester.lastName].filter(Boolean).join(' ').trim();
  if (!name) return { primary: requester.email };

  return { primary: name, secondary: requester.email };
}
